import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation } from 'react-router-dom';

import { useQuery } from "@tanstack/react-query";
import { fetchOpportunityById, updateOpportunityFields, updateiQuoteFields } from "../features/opportunities/data/opportunitiesService";
import { getCalculatedPropertiesForItems, getDefaultInputValues } from "../features/quotes/functions/quoteFunctions";
import { fetchModulesList, fetchModuleData } from "../features/modules/data/moduleService";
import { executeLogic } from "../features/flowBuilder/data/quoteGenerationService";
import { toast } from "react-toastify";
import CopyToClipboardButton from "../components/CopyToClipboardButton";

const SalesforceSyncContext = createContext(null);

export const SalesforceSyncProvider = ({ children }) => {

    const [salesforceSyncModule, setSalesforceSyncModule] = useState(null);
    const [salesforceSyncState, setSalesforceSyncState] = useState("Initialising"); // "Idle" | "Failed" | "Initialising" | "Reading" | "Syncing" | "Error" | "Warning"
    const [salesforceSyncMessage, setSalesforceSyncMessage] = useState("");
    const [executingBy, setExecutingBy] = useState(null);
    const [syncSubject, setSyncSubject] = useState(null);
    const [lastRun, setLastRun] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    
    const location = useLocation();
    const isExecutingRef = useRef(false);
    const isCancelledRef = useRef(false);
    const salesforceSyncModuleRef = useRef(null);
    const isLoadingModulesRef = useRef(true);

    const cancelSync = () => {
        isCancelledRef.current = true;
        setIsCancelling(true);
    };

    const dismissError = () => {
        setSalesforceSyncState("Idle");
        setSyncSubject(null);
    };
    
    // Fetch product modules using useQuery
    const {
        data: modulesData,
        isLoading: isLoadingModules,
    } = useQuery({
        queryKey: ["productModules"],
        queryFn: fetchModulesList,
        staleTime: 60000,
    });

    // When usersData is fetched, update our local state.
    useEffect(() => {

        const findActiveSalesforceSyncModule = async () => {
            if (isLoadingModules) return; // Will stay in "Initialising" state until modules are loaded

            const salesforceModule = modulesData?.find(x => x.isSystemModule === true && x.moduleName === "Salesforce" && x.disabled === false);

            if (!salesforceModule) {
                setSalesforceSyncState("Failed");
                setSalesforceSyncMessage("Salesforce Module Not Found");
                console.warn("Salesforce sync module not found in modules list.");
                return;
            }

            let detailedModuleData = await fetchModuleData(salesforceModule.id);

            if (!detailedModuleData) {
                setSalesforceSyncState("Failed");
                setSalesforceSyncMessage("Salesforce Module Not Found");
                console.warn("Failed to fetch detailed data for Salesforce sync module.");
                return;
            }

            let activeModuleRevision = detailedModuleData.moduleRevisions.find(x => x.disabled === false);

            if (!activeModuleRevision) {
                setSalesforceSyncState("Failed");
                setSalesforceSyncMessage("Salesforce Module Not Found");
                console.warn("No active revision found for Salesforce sync module.");
                return;
            }

            let moduleLogic = JSON.parse(activeModuleRevision.moduleLogicJson);

            setSalesforceSyncModule({
                moduleId: salesforceModule.id,
                name: salesforceModule.moduleName,
                moduleRevisionId: activeModuleRevision.id,
                inputs: moduleLogic.form.inputs,
                postProcessingLogic: moduleLogic.processing.postProcessingLogic,
            });

            setSalesforceSyncState("Idle");
            console.log("Fetched product modules, found salesforce sync module:", salesforceModule);
        }

        findActiveSalesforceSyncModule();
 
    }, [modulesData]);

    useEffect(() => { salesforceSyncModuleRef.current = salesforceSyncModule; }, [salesforceSyncModule]);
    useEffect(() => { isLoadingModulesRef.current = isLoadingModules; }, [isLoadingModules]);

    const syncToSalesforce = async ({quote, quoteRevision, quoteItems, executedBy}) => {
        const opportunityId = quote?.salesforceOpportunityId;

        const outcome = ({state, message}) => {
            const result = { state, message, opportunityId: quote?.salesforceOpportunityId };

            setSalesforceSyncState(["Failed", "Error", "Warning"].includes(state) ? state : "Idle");
            setSalesforceSyncMessage(message);
            setSyncSubject(null);
            setIsCancelling(false);
            isExecutingRef.current = false;
            isCancelledRef.current = false;

            setLastRun({time: new Date(), result: state, detail: message, executedBy});
            setExecutingBy(null);

            console.log("Salesforce sync result:", result);
            return result;
        };

        if (!Array.isArray(quoteItems)) {
            return outcome({state: "Error", message: "Invalid quote items"});
        }

        if (!quote?.isActive) {
            return outcome({state: "Warning", message: "Not active quote. Salesforce sync disabled"});
        }

        if (!quoteRevision?.isMain) {
            return outcome({state: "Warning", message: "Not main quote revision. Salesforce sync disabled"});
        }

        let moduleToUse = salesforceSyncModuleRef.current;
        if (!moduleToUse) {
            if (isLoadingModulesRef.current) {
                // Modules are still loading — wait up to 10 seconds for them to finish
                moduleToUse = await new Promise((resolve) => {
                    const giveUp = setTimeout(() => { clearInterval(poll); resolve(null); }, 10000);
                    const poll = setInterval(() => {
                        if (!isLoadingModulesRef.current) {
                            clearInterval(poll);
                            clearTimeout(giveUp);
                            resolve(salesforceSyncModuleRef.current);
                        }
                    }, 200);
                });
            }

            if (!moduleToUse) {
                return outcome({state: "Failed", message: "Salesforce sync module not found"});
            }
        }

        if (isExecutingRef.current) {
            // A sync is already in progress — wait up to 30 seconds for it to finish before proceeding
            const didFinish = await new Promise((resolve) => {
                const giveUp = setTimeout(() => { clearInterval(poll); resolve(false); }, 30000);
                const poll = setInterval(() => {
                    if (!isExecutingRef.current) {
                        isExecutingRef.current = true; // Claim the lock immediately here        
                        clearInterval(poll);
                        clearTimeout(giveUp);
                        resolve(true);
                    }
                }, 200);
            });
            if (!didFinish) {
                return outcome({state: "Warning", message: "Timed out waiting for previous Salesforce sync to complete"});
            }
        } else {
            isExecutingRef.current = true; // Claim the lock immediately here        
        }

        setExecutingBy(executedBy);

        if (isCancelledRef.current) return outcome({state: "Cancelled", message: ""});

        isCancelledRef.current = false;
        setLastRun(null);
        setSalesforceSyncState("Checking quote and opportunity status");
        setSalesforceSyncMessage("");
        setSyncSubject({
            quoteName: quote?.name ?? "",
            quoteId: quote?.id ?? "",
            revisionNumber: quoteRevision?.revisionNumber ?? "",
        });

        // These two parameters are used for validation purposes, but aren't used by salesforce sync logic directly, so we can pass empty arrays
        const itemLookups = []
        const salesforceCodes = []

        const quoteItemsWithCalculatedProperties = getCalculatedPropertiesForItems(quoteItems, itemLookups, salesforceCodes);

        const defaultInputValuesForModule = getDefaultInputValues(moduleToUse.inputs)
        const inputGuidsForModule = moduleToUse.inputs.map(input => input.guid);
        const existingInputs = quoteRevision?.quoteCustomisation?.basketProcessingInputValues?.filter(x => inputGuidsForModule.includes(x.inputGuid)) ?? []
        const missingInputs = defaultInputValuesForModule.filter(defaultInput =>
            !existingInputs.some(existing => existing.inputGuid === defaultInput.inputGuid)
        );


        console.log("Executing Salesforce sync module");

        setSalesforceSyncState("Reading opportunity state from Salesforce");

        const opportunity = await fetchOpportunityById(opportunityId);
        if (isCancelledRef.current) return outcome({state: "Cancelled", message: ""});

        if (!opportunity?.records) {
            return outcome({state: "Error", message: "Unable to load Salesforce Opportunity. Sync aborted."});
        }

        const opportunityStage = opportunity.records.StageName || "Unknown";
        const salesforceOpportunityIsClosed = opportunityStage.toLowerCase().startsWith("closed") || opportunityStage.toLowerCase() === "opportunity dead";

        if (opportunityStage.toLowerCase() === "unknown") {
            return outcome({state: "Error", message: "Unable to determine Salesforce Opportunity state. Sync aborted."});
        }

        if (salesforceOpportunityIsClosed) {
            return outcome({state: "Error", message: "Opportunity state does not allow updates"});
        }

        setSalesforceSyncState("Reading basket items");

        var result = await executeLogic({
            logic: moduleToUse.postProcessingLogic,
            formInputs: moduleToUse.inputs,
            userInputs: [...existingInputs, ...missingInputs],
            moduleRevisionId: moduleToUse.moduleRevisionId,
            basketItems: quoteItemsWithCalculatedProperties,
            moduleName: moduleToUse.name
        })

        if (isCancelledRef.current) return outcome({state: "Cancelled", message: ""});

        var opportunityFieldsToUpdate = result.salesforceFields
            .filter(field => field?.objectName.toLowerCase() === "opportunity")
            .map(field => ({
                name: field.fieldName,
                value: field.value,
                objectName: field.objectName
            }))

        if (opportunityFieldsToUpdate.length > 0) {
            setSalesforceSyncState("Syncing Opportunity Properties");

            const iQuoteURL = `${window.location.origin}/opportunities/${opportunityId}/${quote.id}/${quoteRevision.id}`;
            opportunityFieldsToUpdate = [...opportunityFieldsToUpdate, { name: "iQuote_Link__c", value: iQuoteURL, objectName: "opportunity" }];

            var updateOpportunityFieldsResult = await updateOpportunityFields(opportunityId, opportunityFieldsToUpdate);
            if (isCancelledRef.current) return outcome({state: "Cancelled", message: ""});
            if (updateOpportunityFieldsResult === "success") {
                console.log("✔️ Updating Salesforce opportunity fields" + JSON.stringify(opportunityFieldsToUpdate));
            } else {
                try {
                    let parsedResult = JSON.parse(updateOpportunityFieldsResult);
                    toast.error(<div><div><strong>Failed to update Salesforce Opportunity fields.</strong></div><div className="mt-3 fst-italic">{parsedResult[0].message}<CopyToClipboardButton text={parsedResult[0].message} /></div></div>, { timeOut: 30000 });
                } catch (error) {
                    toast.error("Failed to update Salesforce Opportunity fields.");
                }
            }
        }

        var iQuoteFieldsToUpdate = result.salesforceFields
            .filter(field => field?.objectName?.toLowerCase() === "iquote")
            .map(field => ({
                name: field.fieldName,
                value: field.value,
                objectName: field.objectName
            }))

        if (iQuoteFieldsToUpdate.length > 0) {
            setSalesforceSyncState("Syncing iQuote Properties");
            let updateiQuoteFieldsResult = await updateiQuoteFields(opportunityId, iQuoteFieldsToUpdate);
            if (updateiQuoteFieldsResult === "success") {
                console.log("✔️ Updating Salesforce iQuote fields" + JSON.stringify(iQuoteFieldsToUpdate));
            } else {
                try {
                    let parsedResult = JSON.parse(updateiQuoteFieldsResult);
                    toast.error(<div><div><strong>Failed to update Salesforce iQuote fields.</strong></div><div className="mt-3 fst-italic">{parsedResult[0].message}<CopyToClipboardButton text={parsedResult[0].message} /></div></div>, { timeOut: 30000 });
                } catch (error) {
                    toast.error("Failed to update Salesforce iQuote fields.");
                }
            }
        }

        console.log(`Salesforce sync module finished for quote "${quote.name}" (${quote.id}) revision ${quoteRevision.revisionNumber} in opportunity "${opportunity.records.Name}" (${opportunityId})`);

        isExecutingRef.current = false;

        return outcome({state: "Success", message: ""});
    }

    return (
        <SalesforceSyncContext.Provider value={{ salesforceSyncState, executingBy, salesforceSyncMessage, syncSubject, lastRun, isCancelling, syncToSalesforce, cancelSync, dismissError }}>
            {children}
        </SalesforceSyncContext.Provider>
    );
};

export const useSalesforceSyncContext = () => {
    const ctx = useContext(SalesforceSyncContext);
    if (!ctx) throw new Error("useSalesforceSyncContext must be used within a SalesforceSyncProvider");
    return ctx;
};

